import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  FileText, Check, X, Download, Eye, Clock, 
  CheckCircle, XCircle, AlertCircle, Send
} from 'lucide-react';
import { toast } from 'sonner';

const LettresMissionPage = ({ user }) => {
  const [lettres, setLettres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLettre, setSelectedLettre] = useState(null);
  const [showRefuseModal, setShowRefuseModal] = useState(false);
  const [motifRefus, setMotifRefus] = useState('');

  const isFormateur = user?.role === 'formateur';
  const isCentre = user?.role === 'centre';

  useEffect(() => {
    loadLettres();
  }, []);

  const loadLettres = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/lettres-mission');
      setLettres(response.data || []);
      // Marquer les notifications lettres comme lues à l'ouverture
      await axios.put('/notifications/mark-read-by-type/lettre_mission').catch(() => {});
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPdf = async (lettreId) => {
    try {
      const response = await axios.get(`/lettres-mission/${lettreId}/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erreur lors de l\'ouverture du PDF');
    }
  };

  const handleDownloadPdf = async (lettreId, formateurNom) => {
    try {
      const response = await axios.get(`/lettres-mission/${lettreId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lettre_mission_${formateurNom}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleAccept = async (lettreId) => {
    try {
      await axios.post(`/lettres-mission/${lettreId}/accepter`);
      toast.success('Lettre de mission acceptée et signée');
      loadLettres();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'acceptation');
    }
  };

  const handleRefuse = async (lettreId) => {
    try {
      await axios.post(`/lettres-mission/${lettreId}/refuser`, { motif: motifRefus });
      toast.success('Lettre de mission refusée');
      setShowRefuseModal(false);
      setMotifRefus('');
      setSelectedLettre(null);
      loadLettres();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du refus');
    }
  };

  const handleSend = async (lettreId) => {
    try {
      await axios.post(`/lettres-mission/${lettreId}/envoyer`);
      toast.success('Lettre de mission envoyée au formateur');
      loadLettres();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const handleDelete = async (lettreId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette lettre ?')) return;
    try {
      await axios.delete(`/lettres-mission/${lettreId}`);
      toast.success('Lettre de mission supprimée');
      loadLettres();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (statut) => {
    switch (statut) {
      case 'brouillon':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
            <Clock className="h-3 w-3" />
            Brouillon
          </span>
        );
      case 'envoyee':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
            <Send className="h-3 w-3" />
            Envoyée
          </span>
        );
      case 'acceptee':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
            <CheckCircle className="h-3 w-3" />
            Acceptée
          </span>
        );
      case 'refusee':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
            <XCircle className="h-3 w-3" />
            Refusée
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8DC63F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lettres-mission-page">
      <div>
        <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">
          {isFormateur ? 'Mes Lettres de Mission' : 'Lettres de Mission'}
        </h1>
        <p className="text-slate-600">
          {isFormateur 
            ? 'Consultez et signez vos lettres de mission'
            : 'Gérez les lettres de mission envoyées aux formateurs'
          }
        </p>
      </div>

      {lettres.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-8 text-center">
          <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Aucune lettre de mission</p>
        </div>
      ) : (
        <div className="space-y-4">
          {lettres.map((lettre) => (
            <div 
              key={lettre.lettre_id}
              className={`bg-white border rounded-lg p-5 shadow-sm ${
                lettre.statut === 'acceptee' 
                  ? 'border-green-300 bg-green-50' 
                  : lettre.statut === 'refusee'
                  ? 'border-red-300 bg-red-50'
                  : 'border-slate-200'
              }`}
              data-testid={`lettre-${lettre.lettre_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FileText className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0F172A]">
                        {isFormateur 
                          ? `Mission de ${lettre.centre_nom}`
                          : `${lettre.formateur_prenom} ${lettre.formateur_nom}`
                        }
                      </h3>
                      <p className="text-sm text-slate-500">
                        {lettre.intitule_formation}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-3 text-sm text-slate-600 mt-3">
                    {lettre.duree && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {lettre.duree}
                      </span>
                    )}
                    {lettre.dates && (
                      <span>📅 {lettre.dates}</span>
                    )}
                    {(lettre.montant_total || lettre.montant) && (
                      <span className="text-green-600 font-medium">💰 {lettre.montant_total || lettre.montant} €</span>
                    )}
                  </div>
                  
                  <div className="mt-3">
                    {getStatusBadge(lettre.statut)}
                    <span className="text-xs text-slate-400 ml-3">
                      Créée le {new Date(lettre.created_at).toLocaleDateString('fr-FR')}
                    </span>
                    {lettre.statut === 'acceptee' && lettre.date_signature_formateur && (
                      <span className="text-xs text-green-600 ml-3">
                        | Signée le {new Date(lettre.date_signature_formateur).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-2 ml-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewPdf(lettre.lettre_id)}
                    className="text-blue-600 border-blue-200"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Voir
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownloadPdf(lettre.lettre_id, `${lettre.formateur_nom}_${lettre.formateur_prenom}`)}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Télécharger
                  </Button>

                  {/* Actions pour le Centre */}
                  {isCentre && lettre.statut === 'brouillon' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => handleSend(lettre.lettre_id)}
                        className="bg-[#8DC63F] hover:bg-[#7db336]"
                      >
                        <Send className="h-4 w-4 mr-1" />
                        Envoyer
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(lettre.lettre_id)}
                        className="text-red-600 border-red-200"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Supprimer
                      </Button>
                    </>
                  )}
                  
                  {/* Info pour le Centre quand la lettre est acceptée */}
                  {isCentre && lettre.statut === 'acceptee' && (
                    <span className="text-xs text-green-600 text-center font-medium">
                      ✓ Signée par le formateur
                    </span>
                  )}

                  {/* Actions pour le Formateur */}
                  {isFormateur && lettre.statut === 'envoyee' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => handleAccept(lettre.lettre_id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Accepter
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedLettre(lettre);
                          setShowRefuseModal(true);
                        }}
                        className="text-red-600 border-red-200"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Refuser
                      </Button>
                    </>
                  )}

                  {lettre.statut === 'acceptee' && (
                    <span className="text-xs text-green-600 text-center">
                      ✓ Signée le {new Date(lettre.date_signature_formateur).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de refus */}
      {showRefuseModal && selectedLettre && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Refuser la lettre de mission
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              Êtes-vous sûr de vouloir refuser cette lettre de mission de {selectedLettre.centre_nom} ?
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Motif du refus (optionnel)
              </label>
              <textarea
                value={motifRefus}
                onChange={(e) => setMotifRefus(e.target.value)}
                className="w-full h-24 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Expliquez la raison du refus..."
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRefuseModal(false);
                  setMotifRefus('');
                  setSelectedLettre(null);
                }}
              >
                Annuler
              </Button>
              <Button
                onClick={() => handleRefuse(selectedLettre.lettre_id)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Confirmer le refus
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LettresMissionPage;
